import FacebookLogin, {
  LoginResponse,
} from '@greatsumini/react-facebook-login';
import CircularProgress from '@mui/material/CircularProgress';
import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import Link from 'next/link';
import Router from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../../hooks/auth';
import { FormProvider, useForm } from '../../hooks/form';

import facebookIcon from '../../assets/facebook-social-icon.png';
import googleIcon from '../../assets/google-social-icon.png';
import LockIcon from '../../assets/lock.svg';
import Logo from '../../assets/logo-novo-cointimes.svg';
import MailIcon from '../../assets/mail.svg';
import UserIcon from '../../assets/user.svg';

import { t } from 'i18next';
import Head from 'next/head';
import { CodeField } from '../../components/ForgotPassword/Steps';
import { Input } from '../../components/UI';
import { FlatButton } from '../../components/UI/Button/styles';
import { SignupForm } from '../../models/auth';
import { Container } from '../../styles/pages/login/styles';

const SignUp: React.FC<React.PropsWithChildren<unknown>> & { layout: string } = () => {
  const [loading, setLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const {
    signup,
    socialLogin,
    setConfirmEmail,
    data,
    login,
    confirmEmail,
  } = useAuth();

  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [errorCode, setErrorCode] = useState('');

  const methods = useForm();
  const {
    handleSubmit,
    formState: { errors },
    watch,
  } = methods;

  const password = useRef({});
  password.current = watch('password', '');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('referral_code');

    if (referralCode) {
      localStorage.setItem('referral_code', referralCode);
    }

  }, []);

  const handleSignup = async (data: SignupForm) => {
    const { email } = data;
    const referralCode = localStorage.getItem('referral_code');

    if (referralCode) {
      data.referral_code = referralCode;
    }

    try {
      setLoading(true);

      await signup(data);

      setConfirmEmail({
        ...data,
        user_email: email,
        code: '',
      });

    } catch (error) {
      setLoading(false);

      if (error?.message) {
        setSignupError(error.message);
      }
    }
  };

  const onGoogleSignup = async (data: TokenResponse) => {
    if (data.access_token) {
      try {
        setLoading(true);

        await socialLogin({
          provider: 'google',
          access_token: data.access_token ?? '',
          referral_code: localStorage.getItem('referral_code'),
        });

        const device_id = localStorage.getItem('@coingoback:currentDeviceIdToLink');

        if (device_id) {
          Router.push(`/device?device_id=${device_id}`);
        } else {
          Router.push('/suggest-extension');
        }

      } catch (error) {
        setLoading(false);

        if (error?.message) {
          setSignupError(error.message);
        }
      }
    }
  };

  const onFacebookSignup = async (data: LoginResponse['authResponse']) => {
    if (data.accessToken) {
      try {
        setLoading(true);

        await socialLogin({
          provider: 'facebook',
          access_token: data.accessToken ?? '',
          referral_code: localStorage.getItem('referral_code'),
        });

        const device_id = localStorage.getItem('@coingoback:currentDeviceIdToLink');

        if (device_id) {
          Router.push(`/device?device_id=${device_id}`);
        } else {
          Router.push('/suggest-extension');
        }

      } catch (error) {
        setLoading(false);

        if (error?.message) {
          setSignupError(error.message);
        }
      }
    }
  };

  const onSubmit = async values => {
    const { user_email, password } = data;

    try {
      setIsLoadingCode(true);
      await confirmEmail({
        user_email,
        code: values.recover_code,
      });
      await login({
        username: user_email,
        password: password,
      });
      setIsLoadingCode(false);

      const device_id = localStorage.getItem('@coingoback:currentDeviceIdToLink');

      if (device_id) {
        Router.push(`/device?device_id=${device_id}`);
      } else {
        Router.push('/suggest-extension');
      }

    } catch (err) {
      setIsLoadingCode(false);
      setErrorCode('Código Inválido');
      console.log(`request failed: ${err}`);
    }
  };

  const prevFormStep = () => {
    setConfirmEmail({ user_email: '' });
  };

  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleSignup,
  });

  return (
    (<Container>
      <Head>
        <title>{t('pages.signup.sign-up')} | Cointimes</title>
        <meta
          name="description"
          content="Comece agora a ganhar bitcoin todo dia navegando, lendo notícias, assistindo vídeos e comprando nas maiores lojas do Brasil."
        />
      </Head>
      <div className="content">

        {data.user_email ? (
          <CodeField
            errorCode={errorCode}
            onSubmit={onSubmit}
            userEmail={data.user_email}
            loading={isLoadingCode}
            prevFormStep={prevFormStep}
          />
        ) : (
          <div className="rightContent">
            <Link href="/" passHref>
              <div className="logo">
                <Logo height={50} />
              </div>
            </Link>
            <span className="subTitle">{t('pages.signup.connect-with-your-social-network')}</span>
            <div className="socialButtons">
              <FacebookLogin
                appId={process.env.NEXT_PUBLIC_FACEBOOK_CLIENT_ID || ''}
                onSuccess={onFacebookSignup}
                render={({ onClick }) => (
                  <button className="facebook" onClick={onClick}>
                    <img src={facebookIcon.src} alt="Facebook Icon" />
                  </button>
                )}
              />
              <button className="google" onClick={() => googleLogin()}>
                <img src={googleIcon.src} alt="Google Icon" />
              </button>
            </div>
            <span className="subTitle">{t('pages.signup.or-use-your-email-address')}</span>

            <FormProvider {...methods}>
              <form className="register" onSubmit={handleSubmit(handleSignup)}>
                <div className="alignInput">
                  <Input.Default
                    icon={<UserIcon />}
                    type="text"
                    placeholder={t('pages.signup.full-name-input')}
                    registerField={{
                      name: 'name',
                      options: {
                        required: t('pages.signup.full-name-input-required'),
                      },
                    }}
                  />
                </div>
                <div className="alignInput">
                  <Input.Default
                    icon={<MailIcon />}
                    type="text"
                    placeholder={t('pages.signup.email-input')}
                    registerField={{
                      name: 'email',
                      options: {
                        required: t('pages.signup.email-input-required'),
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t('pages.signup.email-input-invalid'),
                        },
                      },
                    }}
                  />
                </div>
                <div className="alignInput">
                  <Input.Default
                    icon={<LockIcon />}
                    type="password"
                    placeholder={t('pages.signup.password-input')}
                    registerField={{
                      name: 'password',
                      options: {
                        required: t('pages.signup.password-input-required'),
                        minLength: {
                          value: 6,
                          message: t('pages.signup.password-input-min-six-char'),
                        },
                      },
                    }}
                  />
                </div>

                {errors.name ? (
                  <span className="error">{errors.name.message}</span>
                ) : errors.email ? (
                  <span className="error">{errors.email.message}</span>
                ) : errors.password ? (
                  <span className="error">{errors.password.message}</span>
                ) : errors.confirmPassword ? (
                  <span className="error">
                    {errors.confirmPassword.message}
                  </span>
                ) : (
                  signupError !== '' && (
                    <span className="error">{signupError}</span>
                  )
                )}
                <FlatButton className="formButton" type="submit">
                  {loading ? (
                    <CircularProgress size={24} className="loading" />
                  ) : (
                    t('pages.signup.create-account')
                  )}
                </FlatButton>
                <span className="subTitle">
                  {t('pages.signup.by-registering-you-agree-to-our')}{' '}
                  <a
                    href="/html/CoinGoBackPoliticadePrivacidade.html"
                    target="_blank"
                  >
                    {t('pages.signup.terms-of-use-and-privacy-policy')}
                  </a>
                </span>
              </form>
            </FormProvider>
            <div className="divider padding"></div>
            <Link href="/login" passHref>
              <span className="subTitle black">
                {t('pages.signup.already-have-an-account')} <strong> {t('pages.signup.log-in')} </strong>
              </span>
            </Link>
          </div>
        )}

        {/* <div className="leftContent" style={{marginRight: 20}}>
          <img src={campaign58DiasComCrypto} style={{maxWidth: '100%', minWidth: '100%'}}/>
        </div> */}
      </div>
    </Container>)
  );
};

SignUp.layout = 'login';
export default SignUp;
